import { defineStore } from "pinia";
import { $fetchBackend, logger } from "#imports";
import uploadFile from "~/utils/UploadFile";
import type {
  CheckTypeHebergementBody,
  CheckTypeHebergementResponse,
  HebergementDto,
  HebergementFunnelOrigin,
  PostSiteResponse,
  SiteDto,
  SiteSimilariteResult,
  UniteHebergementDto,
} from "@vao/shared-bridge";
import { FUNCTIONAL_ERRORS, FunctionalException } from "@vao/shared-bridge";
import { HebergementService } from "~/services/hebergementService";
import type {
  InformationsSiteFormValues,
  SiteFormValidationValues,
} from "~/components/hebergements/siteFormValidation";
import { mapHebergementDetailToUnitePayload } from "~/components/hebergements/hebergementDetails";
import type { HebergementDetailValues } from "~/components/hebergements/hebergementDetails";
import {
  mapVisiteToUnitePayload,
  type VisiteTransportFormValues,
} from "~/components/hebergements/hebergementVisiteTransport";
import { useOrganismeStore } from "~/stores/organisme";

const log = logger("stores/hebergement");

type IdResponse = { id?: number };
type HebergementsBySirenResponse = { hebergements?: HebergementDto[] };
type HebergementsListResponse = { rows: HebergementDto[]; total: number };

interface HebergementFunnelContext {
  origin: HebergementFunnelOrigin;
  sejourId?: number | null;
}
interface HebergementStoreState {
  hebergements: HebergementDto[];
  hebergementsTotal: number;
  hebergementCourant: HebergementDto | null;
  siteCourant: PostSiteResponse | null;
  unitesHebergementCourantes: UniteHebergementDto[];
  funnelContext: HebergementFunnelContext | null;
}

export const useHebergementStore = defineStore("hebergement", {
  state: (): HebergementStoreState => ({
    hebergements: [],
    hebergementsTotal: 0,
    hebergementCourant: null,
    siteCourant: null,
    unitesHebergementCourantes: [],
    funnelContext: null,
  }),
  actions: {
    setFunnelOrigin(
      origin: HebergementFunnelOrigin,
      context: Omit<HebergementFunnelContext, "origin"> = {},
    ) {
      log.d("setFunnelOrigin", { origin, context });
      this.funnelContext = { origin, ...context };
    },
    resetFunnelContext() {
      this.funnelContext = null;
    },
    async fetchBySiren(siren: string) {
      try {
        log.i("fetchBySiren - IN");
        const { hebergements } = (await $fetchBackend(
          `/hebergement/siren/${siren}`,
          {
            credentials: "include",
          },
        )) as HebergementsBySirenResponse;
        if (hebergements) {
          this.hebergements = hebergements;
        }
        log.d("fetchBySiren  - DONE");
      } catch (err: unknown) {
        this.hebergements = [];
        log.i("fetchBySiren - DONE with error", err);
      }
    },
    async fetch(params = {}) {
      try {
        log.i("fetch - IN");
        const { rows, total } = (await $fetchBackend("/hebergement", {
          credentials: "include",
          params,
        })) as HebergementsListResponse;

        this.hebergements = rows;
        this.hebergementsTotal = total;
        log.d("fetch  - DONE");
      } catch (err: unknown) {
        this.hebergements = [];
        log.i("fetch - DONE with error", err);
      }
    },
    async fetchById(id: number) {
      try {
        log.i("fetchById - IN", { id });

        const hebergement = await HebergementService.getHebergement(id);
        log.d("fetchById - FETCH", hebergement);
        if (hebergement) {
          this.hebergementCourant = hebergement;
        }
        log.d("fetchById - DONE");
      } catch (err: unknown) {
        this.hebergementCourant = null;
        log.i("fetchById - DONE with error", err);
      }
    },
    async checkSiteSimilarites(site: SiteDto): Promise<SiteSimilariteResult[]> {
      const organismeCourant = useOrganismeStore().organismeCourant;
      const organismeId = Number(organismeCourant?.organismeId);
      if (!Number.isFinite(organismeId) || organismeId <= 0) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.ORGANISME_COURANT_NOT_FOUND,
        );
      }
      try {
        log.i("checkSiteSimilarites - IN", { site });
        const similarites = await HebergementService.checkSiteSimilarites({
          ...site,
          organismeId,
        });
        log.d("checkSiteSimilarites - DONE", similarites);
        return similarites;
      } catch (err: unknown) {
        log.i("checkSiteSimilarites - DONE with error", err);
        throw err;
      }
    },
    async checkTypeHebergement(
      body: CheckTypeHebergementBody,
    ): Promise<CheckTypeHebergementResponse> {
      log.i("checkTypeHebergement - IN", body);
      try {
        const result = await HebergementService.checkTypeHebergement(body);
        log.d("checkTypeHebergement - DONE", result);
        return result;
      } catch (err: unknown) {
        log.i("checkTypeHebergement - DONE with error", err);
        throw err;
      }
    },
    async getSite(siteId: string): Promise<boolean> {
      log.i("getSite - IN", { siteId });
      const organismeCourant = useOrganismeStore().organismeCourant;
      if (!organismeCourant) {
        log.w("getSite - organismeCourant non renseigné");
        this.siteCourant = null;
        return false;
      }

      const organismeId = Number(organismeCourant?.organismeId);
      if (!Number.isFinite(organismeId) || organismeId <= 0) {
        log.w("getSite - organismeId invalide", { organismeId });
        this.siteCourant = null;
        return false;
      }

      try {
        const { site, unitesHebergement } = await HebergementService.getSite(
          siteId,
          organismeId,
        );
        if (!site) {
          log.w("getSite - site introuvable", { siteId });
          this.siteCourant = null;
          this.unitesHebergementCourantes = [];
          return false;
        }
        this.siteCourant = site;
        this.unitesHebergementCourantes = unitesHebergement;
        log.d("getSite - DONE", { siteId });
        return true;
      } catch (err: unknown) {
        this.siteCourant = null;
        this.unitesHebergementCourantes = [];
        log.w("getSite - FAIL", { siteId, err });
        return false;
      }
    },
    resetSiteCourant() {
      this.siteCourant = null;
      this.unitesHebergementCourantes = [];
    },
    async postSite(site: SiteFormValidationValues): Promise<PostSiteResponse> {
      log.i("postSite - IN", { site });
      const organismeCourant = useOrganismeStore().organismeCourant;
      if (!site.nomSiteOfficiel) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.SITE_NOM_OFFICIEL_OBLIGATOIRE,
        );
      }
      if (!site.adresse) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.SITE_ADRESSE_OBLIGATOIRE,
        );
      }

      const organismeId = Number(organismeCourant?.organismeId);
      if (!Number.isFinite(organismeId) || organismeId <= 0) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.ORGANISME_COURANT_NOT_FOUND,
        );
      }
      try {
        const createdSite = await HebergementService.postSite({
          adresse: site.adresse,
          deplacementProximiteDescription: null,
          descriptif: null,
          excursionDescription: null,
          forceNew: Boolean(site.forceNew),
          hebergementTypeId: null,
          nomSite: site.nomSiteOrganisme || site.nomSiteOfficiel,
          nomSiteOfficiel: site.nomSiteOfficiel,
          organismeId,
          respEmail: null,
          respNomPrenom: null,
          respTelephone: null,
          vehiculesAdaptes: null,
        });
        log.d("postSite - DONE", { siteId: createdSite.siteId });
        this.siteCourant = { ...createdSite, adresse: site.adresse };
        return createdSite;
      } catch (err: unknown) {
        log.i("postSite - DONE with error", err);
        throw err;
      }
    },
    async patchSite(
      siteId: string,
      values: InformationsSiteFormValues,
    ): Promise<void> {
      log.i("patchSite - IN", { siteId, values });
      const organismeCourant = useOrganismeStore().organismeCourant;
      try {
        await HebergementService.patchSite(siteId, {
          description: values.description,
          hebergementTypeValue: values.typeHebergement,
          organismeId: Number(organismeCourant?.organismeId),
          responsable: {
            email: values.responsable.email,
            nomPrenom: values.responsable.nomPrenom,
            telephone: values.responsable.telephone,
          },
        });
        log.d("patchSite - DONE");
      } catch (err: unknown) {
        log.i("patchSite - DONE with error", err);
        throw err;
      }
    },
    async updateOrCreate(
      hebergement: Record<string, unknown>,
      hebergementId: number,
    ) {
      log.i("updateOrCreate - IN", { hebergement });

      const url = hebergementId
        ? `/hebergement/${hebergementId}`
        : `/hebergement`;

      const { id } = (await $fetchBackend(url, {
        method: "POST",
        body: hebergement,
        credentials: "include",
      })) as IdResponse;
      log.i("updateOrCreate - Done", { id });
      return id ?? hebergementId;
    },
    async updateOrCreateBrouillon(
      hebergement: Record<string, unknown>,
      hebergementId: number,
    ) {
      log.i("updateOrCreate - IN", { hebergement });

      const url = hebergementId
        ? `/hebergement/${hebergementId}/brouillon`
        : `/hebergement/brouillon`;

      const { id } = (await $fetchBackend(url, {
        method: hebergementId ? "PUT" : "POST",
        body: hebergement,
        credentials: "include",
      })) as IdResponse;
      log.i("updateOrCreate - Done", { id });
      return id ?? hebergementId;
    },
    async activate(
      hebergement: Record<string, unknown>,
      hebergementId: number,
    ) {
      log.i("updateOrCreate - IN", { hebergement });

      const { id } = (await $fetchBackend(
        `/hebergement/${hebergementId}/activate`,
        {
          method: "PUT",
          body: hebergement,
          credentials: "include",
        },
      )) as IdResponse;
      log.i("updateOrCreate - Done", { id });
      return id ?? hebergementId;
    },
    async desactivate(hebergementId: number) {
      log.i("desactivate - IN", { hebergementId });

      const { id } = (await $fetchBackend(
        `/hebergement/${hebergementId}/desactivate`,
        {
          method: "PUT",
          credentials: "include",
        },
      )) as IdResponse;
      log.i("desactivate - Done", { id });
      return id ?? hebergementId;
    },
    async reactivate(hebergementId: number) {
      log.i("reactivate - IN", { hebergementId });

      const { id } = (await $fetchBackend(
        `/hebergement/${hebergementId}/reactivate`,
        {
          method: "PUT",
          credentials: "include",
        },
      )) as IdResponse;
      log.i("desactivate - Done", { id });
      return id ?? hebergementId;
    },
    async uploadAllFiles(hebergement: Record<string, unknown>) {
      const { informationsLocaux } = hebergement;

      if (!informationsLocaux || typeof informationsLocaux !== "object") {
        return;
      }

      const uploadFileIfNeeded = async (
        fieldName: string,
        file: Record<string, unknown> | null,
        logLabel: string,
      ) => {
        if (file && !file.uuid) {
          try {
            const uploadedFileId = await uploadFile(
              fieldName,
              file as unknown as File,
            );
            return {
              uuid: uploadedFileId,
              name: file.name,
              createdAt: new Date(),
            };
          } catch (error: unknown) {
            log.w(logLabel, error);
            throw error;
          }
        }
        return file;
      };

      const locaux = informationsLocaux as Record<string, unknown>;
      if (locaux.reglementationErp) {
        locaux.fileDerniereAttestationSecurite = await uploadFileIfNeeded(
          "attestation_securite",
          (locaux.fileDerniereAttestationSecurite as Record<
            string,
            unknown
          > | null) ?? null,
          "fileDerniereAttestationSecurite",
        );

        locaux.fileDernierArreteAutorisationMaire = await uploadFileIfNeeded(
          "arrete_autorisation_maire",
          (locaux.fileDernierArreteAutorisationMaire as Record<
            string,
            unknown
          > | null) ?? null,
          "fileDernierArreteAutorisationMaire",
        );
      } else {
        locaux.fileReponseExploitantOuProprietaire = await uploadFileIfNeeded(
          "reponse_explouprop",
          (locaux.fileReponseExploitantOuProprietaire as Record<
            string,
            unknown
          > | null) ?? null,
          "fileReponseExploitantOuProprietaire",
        );
      }
    },
    async uploadDetailFiles(
      hebergement: HebergementDetailValues,
    ): Promise<HebergementDetailValues> {
      const uploadIfNeeded = async (
        fieldName: string,
        file: HebergementDetailValues["fileDerniereAttestationSecurite"],
      ): Promise<
        HebergementDetailValues["fileDerniereAttestationSecurite"]
      > => {
        if (file && typeof file === "object" && !("uuid" in file)) {
          const rawFile = file as unknown as File;
          const uuid = await uploadFile(fieldName, rawFile);
          return {
            uuid,
            name: rawFile.name,
            createdAt: new Date(),
          } as HebergementDetailValues["fileDerniereAttestationSecurite"];
        }
        return file;
      };

      const files = await Promise.all([
        uploadIfNeeded(
          "attestation_securite",
          hebergement.fileDerniereAttestationSecurite,
        ),
        uploadIfNeeded(
          "arrete_autorisation_maire",
          hebergement.fileDernierArreteAutorisationMaire,
        ),
        uploadIfNeeded(
          "reponse_explouprop",
          hebergement.fileReponseNonAssujettissementErp,
        ),
      ]);

      return {
        ...hebergement,
        fileDerniereAttestationSecurite: files[0],
        fileDernierArreteAutorisationMaire: files[1],
        fileReponseNonAssujettissementErp: files[2],
      };
    },
    async saveUnites(
      siteId: string,
      hebergements: HebergementDetailValues[],
    ): Promise<void> {
      log.i("saveUnites - IN", { siteId, count: hebergements.length });
      const organismeCourant = useOrganismeStore().organismeCourant;
      const organismeId = Number(organismeCourant?.organismeId);
      if (!Number.isFinite(organismeId) || organismeId <= 0) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.ORGANISME_COURANT_NOT_FOUND,
        );
      }
      try {
        for (const hebergement of hebergements) {
          const detailWithUploadedFiles =
            await this.uploadDetailFiles(hebergement);
          const payload = mapHebergementDetailToUnitePayload(
            detailWithUploadedFiles,
          );
          const existing =
            hebergement.id === undefined
              ? false
              : this.unitesHebergementCourantes.find(
                  ({ id }) => id === hebergement.id,
                );
          if (existing) {
            await HebergementService.updateUniteHebergement(
              siteId,
              existing.id,
              organismeId,
              { ...payload, typePensions: hebergement.pension },
            );
          } else {
            await HebergementService.createUniteHebergement(siteId, {
              ...payload,
              organismeId,
              typePensions: hebergement.pension,
            });
          }
        }
        await this.getSite(siteId);
        log.i("saveUnites - DONE");
      } catch (err: unknown) {
        log.i("saveUnites - DONE with error", err);
        throw err;
      }
    },
    async saveVisiteTransport(
      siteId: string,
      values: VisiteTransportFormValues,
    ): Promise<void> {
      log.i("saveVisiteTransport - IN", { siteId });
      const organismeCourant = useOrganismeStore().organismeCourant;
      const organismeId = Number(organismeCourant?.organismeId);
      if (!Number.isFinite(organismeId) || organismeId <= 0) {
        throw new FunctionalException(
          FUNCTIONAL_ERRORS.ORGANISME_COURANT_NOT_FOUND,
        );
      }
      try {
        const { site: updatedSite, unitesHebergement } =
          await HebergementService.patchSiteVisiteTransport(siteId, {
            deplacementProximiteDescription:
              values.transport.deplacementProximiteDescription || null,
            excursionDescription: values.transport.excursionDescription || null,
            organismeId,
            vehiculesAdaptes: values.transport.vehiculesAdaptes,
            unites: values.unitesVisite
              .filter((visite) => visite.id !== null)
              .map((visite) => ({
                id: visite.id as number,
                ...mapVisiteToUnitePayload(visite),
              })),
          });
        if (updatedSite) {
          this.siteCourant = updatedSite;
        }
        this.unitesHebergementCourantes = unitesHebergement;
        log.i("saveVisiteTransport - DONE");
      } catch (err: unknown) {
        log.i("saveVisiteTransport - DONE with error", err);
        throw err;
      }
    },
  },
});
